import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ShieldCheck, Check, X, Download, HelpCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/** Pagina pubblica delle garanzie: la risposta scritta alla domanda «vedete i miei incassi?». */
export default function Garanzie() {
  const [d, setD] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/public/garanzie`).then((r) => r.json()).then(setD).catch(() => {});
  }, []);

  if (!d) return <div className="min-h-screen bg-slate-950 p-8 text-sm text-slate-500">Caricamento…</div>;

  return (
    <div className="min-h-screen bg-slate-950 px-5 py-10 text-slate-200" data-testid="garanzie-page">
      <div className="mx-auto max-w-3xl">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <a href="/" className="inline-flex items-center gap-1.5 text-sm text-slate-400 transition-colors hover:text-amber-400" data-testid="garanzie-back-link">
            <ArrowLeft className="h-4 w-4" /> Torna al sito
          </a>
          <Button asChild variant="outline" size="sm" className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800">
            <a href={`${API}/api/public/garanzie.pdf`} target="_blank" rel="noreferrer" data-testid="garanzie-pdf-link">
              <Download className="mr-1.5 h-4 w-4" /> Scarica il foglio A4
            </a>
          </Button>
        </div>

        <div className="mt-8 flex items-start gap-3">
          <span className="mt-1 rounded-lg bg-amber-500/10 p-2 text-amber-400">
            <ShieldCheck className="h-5 w-5" />
          </span>
          <div>
            <h1 className="font-head text-4xl font-bold leading-tight text-slate-50 sm:text-5xl">
              Chi vede i tuoi incassi?
              <span className="block text-amber-400">Nessuno, a parte te.</span>
            </h1>
            <p className="mt-1 text-xs text-slate-500">Garanzie versione {d.versione} · {d.data}</p>
          </div>
        </div>

        <p className="mt-6 text-base leading-relaxed text-slate-300">{d.apertura}</p>

        <div className="mt-10 grid gap-4 sm:grid-cols-2">
          <div className="rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.04] p-5" data-testid="garanzie-vedo">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-400">Cosa vede Reprehendo</p>
            <ul className="mt-3 space-y-2">
              {d.vedo.map((x) => (
                <li key={x} className="flex gap-2 text-sm text-slate-300">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" /> {x}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-rose-500/25 bg-rose-500/[0.04] p-5" data-testid="garanzie-non-vedo">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-rose-400">Cosa NON vede, mai</p>
            <ul className="mt-3 space-y-2">
              {d.non_vedo.map((x) => (
                <li key={x} className="flex gap-2 text-sm text-slate-300">
                  <X className="mt-0.5 h-4 w-4 shrink-0 text-rose-400" /> {x}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <h2 className="mt-12 text-base font-semibold text-slate-100 md:text-lg">
          Non è una promessa: è come è costruito
        </h2>
        <ul className="mt-3 space-y-2.5" data-testid="garanzie-come">
          {d.come.map((x) => (
            <li key={x} className="flex gap-3 text-sm leading-relaxed text-slate-400">
              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400" /> {x}
            </li>
          ))}
        </ul>

        <h2 className="mt-12 text-base font-semibold text-slate-100 md:text-lg">Perché conviene prenderlo</h2>
        <div className="mt-3 space-y-3" data-testid="garanzie-perche">
          {d.perche.map((x) => (
            <div key={x.t} className="rounded-xl border border-slate-800 bg-slate-900/40 p-4 transition-colors hover:border-amber-500/40">
              <p className="text-sm font-semibold text-slate-100">{x.t}</p>
              <p className="mt-1 text-sm leading-relaxed text-slate-400">{x.p}</p>
            </div>
          ))}
        </div>

        <h2 className="mt-12 text-base font-semibold text-slate-100 md:text-lg">Se ti stai chiedendo…</h2>
        <div className="mt-3 space-y-3" data-testid="garanzie-domande">
          {d.domande.map((x) => (
            <div key={x.q}>
              <p className="flex gap-2 text-sm font-semibold text-amber-400">
                <HelpCircle className="mt-0.5 h-4 w-4 shrink-0" /> {x.q}
              </p>
              <p className="mt-1 pl-6 text-sm leading-relaxed text-slate-400">{x.a}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 rounded-2xl border border-slate-800 bg-slate-900/40 p-5" data-testid="garanzie-non-promettiamo">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Cosa non ti promettiamo</p>
          <ul className="mt-3 space-y-2">
            {d.non_promettiamo.map((x) => (
              <li key={x} className="text-sm leading-relaxed text-slate-400">{x}</li>
            ))}
          </ul>
        </div>

        <div className="mt-10 flex flex-wrap gap-3">
          <Button asChild className="h-12 bg-amber-500 px-6 font-bold text-slate-950 hover:bg-amber-400">
            <a href="/prova" data-testid="garanzie-prova-btn">Provalo sette giorni</a>
          </Button>
          <Button asChild variant="outline" className="h-12 border-slate-700 bg-transparent px-6 text-slate-300 hover:bg-slate-800">
            <a href="/privacy" data-testid="garanzie-privacy-link">Leggi l'informativa completa</a>
          </Button>
        </div>

        <p className="mt-10 border-t border-slate-800 pt-5 text-xs text-slate-500">
          {d.vendor.nome} · {d.vendor.titolare} · {d.vendor.email} · {d.vendor.sito}
        </p>
      </div>
    </div>
  );
}
